<template>
  <div class="settings-page">
    <div class="page-header">
      <div>
        <h1>{{ $t('menu.admin_settings.page.header') }}</h1>
        <p class="page-subtitle">{{ $t('menu.admin_settings.page.subtitle') }}</p>
      </div>
    </div>

    <div class="settings-grid">
      <!-- Departments -->
      <div class="tap-card settings-section">
        <div class="section-header">
          <h2>{{ $t('menu.admin_settings.page.departments') }}</h2>
          <span class="tap-badge tap-badge-accent">{{ user.departments.length }}</span>
        </div>

        <div class="settings-table-wrap">
          <table class="tap-table">
            <thead>
              <tr>
                <th>{{ $t('menu.admin_settings.page.id') }}</th>
                <th>{{ $t('menu.admin_settings.page.name') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="dept in user.departments" :key="dept.p_id">
                <td><span class="cell-mono">{{ dept.p_id }}</span></td>
                <td>
                  <input v-if="editingDept === dept.p_id"
                         v-model="editDeptName" type="text"
                         class="inline-edit"
                         @keyup.enter="saveDepartment(dept.p_id)"
                         @keyup.escape="editingDept = null" />
                  <span v-else class="cell-name" @dblclick="startEditDept(dept)">{{ dept.name }}</span>
                </td>
                <td class="cell-actions">
                  <button v-if="editingDept === dept.p_id" class="row-action-btn save" @click="saveDepartment(dept.p_id)">
                    <i class="bx bx-check"></i>
                  </button>
                  <button v-if="editingDept === dept.p_id" class="row-action-btn" @click="editingDept = null">
                    <i class="bx bx-x"></i>
                  </button>
                  <button v-if="editingDept !== dept.p_id" class="row-action-btn" @click="startEditDept(dept)">
                    <i class="bx bx-edit-alt"></i>
                  </button>
                  <button v-if="editingDept !== dept.p_id" class="row-action-btn delete" @click="removeDepartment(dept.p_id)">
                    <i class="bx bx-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="add-row">
          <input v-model="newDeptName" type="text" class="tap-input add-input"
                 :placeholder="$t('menu.admin_settings.page.namePlaceholder')"
                 @keyup.enter="addDepartment()" />
          <button class="appButton" @click="addDepartment()">
            <i class="bx bx-plus"></i>
            {{ $t('menu.admin_settings.page.addDepartment') }}
          </button>
        </div>
      </div>

      <!-- User Groups -->
      <div class="tap-card settings-section">
        <div class="section-header">
          <h2>{{ $t('menu.admin_settings.page.userGroups') }}</h2>
          <span class="tap-badge tap-badge-accent">{{ user.roles.length }}</span>
        </div>

        <div class="settings-table-wrap">
          <table class="tap-table">
            <thead>
              <tr>
                <th>{{ $t('menu.admin_settings.page.id') }}</th>
                <th>{{ $t('menu.admin_settings.page.name') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="role in user.roles" :key="role.p_id">
                <td><span class="cell-mono">{{ role.p_id }}</span></td>
                <td>
                  <input v-if="editingRole === role.p_id"
                         v-model="editRoleName" type="text"
                         class="inline-edit"
                         @keyup.enter="saveRole(role.p_id)"
                         @keyup.escape="editingRole = null" />
                  <span v-else class="cell-name" @dblclick="startEditRole(role)">{{ role.name }}</span>
                </td>
                <td class="cell-actions">
                  <button v-if="editingRole === role.p_id" class="row-action-btn save" @click="saveRole(role.p_id)">
                    <i class="bx bx-check"></i>
                  </button>
                  <button v-if="editingRole === role.p_id" class="row-action-btn" @click="editingRole = null">
                    <i class="bx bx-x"></i>
                  </button>
                  <button v-if="editingRole !== role.p_id" class="row-action-btn" @click="startEditRole(role)">
                    <i class="bx bx-edit-alt"></i>
                  </button>
                  <button v-if="editingRole !== role.p_id" class="row-action-btn delete" @click="removeRole(role.p_id)">
                    <i class="bx bx-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="add-row">
          <input v-model="newRoleName" type="text" class="tap-input add-input"
                 :placeholder="$t('menu.admin_settings.page.namePlaceholder')"
                 @keyup.enter="addRole()" />
          <button class="appButton" @click="addRole()">
            <i class="bx bx-plus"></i>
            {{ $t('menu.admin_settings.page.addUserGroup') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useSettings } from '@/store/user'

export default {
  setup() {
    const user = useSettings();
    user.getDepartments();
    user.getRoles();
    return { user }
  },
  data() {
    return {
      newDeptName: '',
      newRoleName: '',
      editingDept: null,
      editingRole: null,
      editDeptName: '',
      editRoleName: '',
    }
  },
  methods: {
    async addDepartment() {
      if (!this.newDeptName.trim()) {
        alert(this.$t('settings.page.nameRequired'));
        return;
      }
      await this.user.addDepartment(this.newDeptName.trim());
      this.newDeptName = '';
    },
    startEditDept(dept) {
      this.editingDept = dept.p_id;
      this.editDeptName = dept.name;
    },
    async saveDepartment(id) {
      if (!this.editDeptName.trim()) return;
      await this.user.updateDepartment(id, this.editDeptName.trim());
      this.editingDept = null;
    },
    async removeDepartment(id) {
      if (confirm(this.$t('settings.page.deleteConfirmDepartment'))) {
        await this.user.deleteDepartment(id);
      }
    },
    async addRole() {
      if (!this.newRoleName.trim()) {
        alert(this.$t('settings.page.nameRequired'));
        return;
      }
      await this.user.addRole(this.newRoleName.trim());
      this.newRoleName = '';
    },
    startEditRole(role) {
      this.editingRole = role.p_id;
      this.editRoleName = role.name;
    },
    async saveRole(id) {
      if (!this.editRoleName.trim()) return;
      await this.user.updateRole(id, this.editRoleName.trim());
      this.editingRole = null;
    },
    async removeRole(id) {
      if (confirm(this.$t('settings.page.deleteConfirmUserGroup'))) {
        await this.user.deleteRole(id);
      }
    },
  }
}
</script>

<style scoped>
.settings-page {
  animation: fadeIn 0.3s ease-out;
}
.page-header {
  margin-bottom: 1.5rem;
}
.page-subtitle {
  color: #9aa0a8;
  font-size: 0.875rem;
  margin-top: 0.125rem;
}

.settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

.settings-section {
  padding: 1.5rem;
}
.section-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.settings-table-wrap {
  margin-bottom: 1.25rem;
}

.cell-mono {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.8125rem;
  color: #5c6370;
}
.cell-name {
  color: #e8eaed;
  font-weight: 500;
  cursor: default;
}
.cell-actions {
  text-align: right;
  white-space: nowrap;
}

.row-action-btn {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  background: rgba(255, 255, 255, 0.03);
  color: #9aa0a8;
  font-size: 1rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: 0.25rem;
}
.row-action-btn:hover {
  background: rgba(117, 35, 35, 0.1);
  border-color: rgba(117, 35, 35, 0.2);
  color: #a63c3c;
}
.row-action-btn.save:hover {
  background: rgba(19, 206, 102, 0.1);
  border-color: rgba(19, 206, 102, 0.2);
  color: #13ce66;
}
.row-action-btn.delete:hover {
  background: rgba(237, 106, 90, 0.1);
  border-color: rgba(237, 106, 90, 0.2);
  color: #ED6A5A;
}

.inline-edit {
  width: 100%;
  padding: 0.35rem 0.5rem;
  font-size: 0.875rem;
}

.add-row {
  display: flex;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}
.add-input {
  flex: 1;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
